import type { Dict } from './vi';

export const en: Dict = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    close: 'Close',
    loading: 'Loading...',
    dismissNotification: 'Dismiss notification',
    notifications: 'Notifications',
    wordmark: 'REDA GALLERY · ARCHIVE & STUDIO',
  },
  errorBoundary: {
    title: 'Connection error occurred',
    body: 'Could not display the exhibition space. Please reload.',
    reload: 'Reload page',
  },
  auth: {
    kicker: 'Reda Gallery · Archival Studio',
    theme: {
      toLight: 'Switch to light theme',
      toDark: 'Switch to dark theme',
    },
    title: {
      login: 'Curator Atelier',
      register: 'Create Access',
    },
    subtitle: {
      login: 'Sign in to curate virtual 3D exhibitions, spatial lighting, and spatial audio salons.',
      register: 'Register as an exhibition curator to compose and publish architectural art galleries.',
    },
    modeSwitch: {
      ariaLabel: 'Authentication selection',
      signIn: 'Sign In',
      register: 'Register',
    },
    google: {
      title: 'Authenticate via Google Workspace',
      cta: 'Continue with Google',
    },
    divider: 'or with credentials',
    fields: {
      fullName: {
        label: 'Curator Full Name',
        placeholder: 'Elena Rostova',
      },
      email: {
        label: 'Curator Email',
        placeholder: 'curator@gallery.org',
      },
      password: {
        label: 'Password Key',
        placeholder: '••••••••••••',
      },
    },
    password: {
      show: 'Show password',
      hide: 'Hide password',
      showShort: 'Show',
      hideShort: 'Hide',
    },
    submit: {
      loading: 'Authenticating…',
      login: 'Sign In with Password',
      register: 'Create Curator Account',
    },
    footer: {
      security: 'Encrypted Session · Cloudflare D1 Architecture',
    },
    errors: {
      loginFailed: 'Login failed',
      registrationFailed: 'Registration failed',
      sessionVerifyFailed: 'Authentication succeeded, but failed to verify session. Please try signing in.',
      networkError: 'Network error during authentication. Check your internet connection.',
    },
    account: {
      backAria: 'Back to dashboard',
      backText: 'Back to exhibitions',
      brandSubtitle: 'Curator account management',
      title: 'Your account',
      description: 'Personal information, curator profile, and security settings.',
      profileTitle: 'Profile information',
      curatorFallback: 'Curator',
      role: 'Role: {role}',
      curatorProfile: 'Curator profile',
      fullNameLabel: 'Full name',
      emailLabel: 'Email',
      emailHint: 'Email used for signing in; cannot be changed.',
      securityTitle: 'Security',
      currentPasswordLabel: 'Current password',
      newPasswordLabel: 'New password',
      newPasswordHint: 'At least 8 characters.',
      confirmPasswordLabel: 'Confirm new password',
      passwordPlaceholder: '••••••••',
      passwordMismatch: "Passwords don't match.",
      submit: 'Change password',
      submitLoading: 'Updating password…',
      toastSuccess: 'Password updated successfully.',
      toastErrorFallback: 'Could not change password. Please check your current password.',
      toastNetworkError: 'Network error while changing password.',
    },
  },
  dashboard: {
    title: 'Your exhibitions',
    accountAria: 'Manage your account',
    newExhibition: 'New exhibition',
    signOut: 'Sign Out',
    loading: 'Loading your exhibitions…',
    emptyAria: 'No exhibitions yet',
    emptyTitle: 'You have no exhibitions yet',
    emptyDescription: 'Start curating your first virtual exhibition — select artworks, craft an artistic narrative, and publish your 3D gallery.',
    emptyCta: 'Create your first exhibition',
    newCardAria: 'Create new exhibition',
    badge: {
      live: 'Live',
      draft: 'Draft',
    },
    card: {
      meta: '{count} works · {room}',
      noRoom: 'No room',
      curator: 'Curator · {name}',
      edit: 'Edit & curate',
      view: 'View 3D',
      share: 'Share',
      shareTitle: 'Share {title}',
      deleteAria: 'Delete {title}',
      confirmDelete: 'Confirm delete',
    },
    newForm: {
      title: 'Create New Exhibition',
      titleLabel: 'Exhibition Title *',
      titlePlaceholder: 'e.g. Modernist Perspectives 2026',
      curatorLabel: 'Curator Name',
      curatorPlaceholder: 'e.g. Elena Rostova',
      spaceLabel: '3D Gallery Space *',
      libraryRoom: 'Platform Library Room',
      customSpace: 'Custom 3D Space (.GLB)',
      selectRoom: 'Select Gallery Room',
      platformLibrarySuffix: '(Platform Library)',
      customRoomSuffix: '(Custom Room)',
      customRoomNameLabel: 'Custom Space Name',
      customRoomNamePlaceholder: 'e.g. Modern Minimalist Pavilion',
      glbLabel: 'Google Drive Link or File ID (.GLB Model) *',
      glbPlaceholder: 'https://drive.google.com/file/d/... or File ID',
      descriptionLabel: 'Curatorial Statement / Description',
      descriptionPlaceholder: 'Exhibition overview...',
      submit: 'Create & Start Curating',
      submitLoading: 'Creating…',
      errors: {
        missingGlb: 'Please provide a Google Drive link, file ID, or 3D model URL for the custom room.',
        createRoomFailed: 'Failed to create custom 3D space: {detail}',
        networkError: 'Network error. Please try again.',
      },
    },
  },
  studio: {
    appLoading: {
      kicker: 'Reda Atelier',
      text: 'Connecting to Curator Vault…',
    },
    transitions: {
      hotspot: {
        arc_dip: {
          label: 'Cinematic Arc Flight',
          description: 'Smooth drone flight with a subtle zoom pullback arc mid-flight.',
        },
        linear_glide: {
          label: 'Linear Pan & Zoom Glide',
          description: 'Smooth direct trajectory panning straight across the canvas.',
        },
        deep_pullback: {
          label: 'Dramatic Overview Pullback',
          description: 'Pulls camera back to full artwork overview before diving in.',
        },
        instant_cut: {
          label: 'Instant Museum Snap',
          description: 'Crisp camera jump with a rapid smooth micro-settle.',
        },
        spring_overshoot: {
          label: 'Elastic Spring Bounce',
          description: 'Dynamic camera flight with an energetic bounce at arrival.',
        },
      },
      intro: {
        fade: {
          label: 'Smooth Crossfade',
          description: 'Classic seamless dissolve into the 3D exhibition room.',
        },
        zoom_in: {
          label: 'Cinematic Push-In Dive',
          description: 'Zooms forward through the video directly into the 3D space.',
        },
        blur_fade: {
          label: 'Dreamy Blur Dissolve',
          description: 'Soft optic blur defocus melting into the sharp 3D gallery.',
        },
        iris_circle: {
          label: 'Circular Iris Reveal',
          description: 'Dramatic aperture circle opening outward from center.',
        },
        slide_up: {
          label: 'Curtain Lift',
          description: 'Theatrical curtain lift sliding up to reveal the space.',
        },
        flash_white: {
          label: 'Gallery Bloom Flash',
          description: 'Bright artistic flash of light blooming into the room.',
        },
      },
    },
    statusBar: {
      workCount: '{count} works',
      mode: 'Mode',
    },
    toolRail: {
      curate: 'Curate',
      rooms: 'Rooms',
      artists: 'Artists',
      setup: 'Setup',
    },
    artistsPane: {
      title: 'Artists',
      addAria: 'Add artist',
      noDates: 'No dates',
    },
    artworksPane: {
      title: 'Artworks',
      addAria: 'Add artwork',
      addTitle: 'Add new artwork',
      placementStatusAria: 'Artwork placement status',
      inRoomTab: 'In Room ({count})',
      inRoomTitle: 'Artworks currently positioned on walls in the 3D room',
      storageTab: 'Storage ({count})',
      storageTitle: 'Artworks stored for later placement',
      filterTitle: 'Filter artworks by artist',
      filterAll: 'Filter: All Artists ({count})',
      filterUnassigned: 'Unassigned ({count})',
      emptyFiltered: 'No artworks match the selected artist filter in this tab.',
      emptyInRoom: 'No artworks in the room. Place an artwork from Storage or click + to add.',
      emptyStorage: 'No stored artworks. Artworks moved from the room will appear here.',
    },
    inspector: {
      resizeTitle: 'Drag to resize inspector width',
      newArtwork: 'New artwork',
      closeAria: 'Close inspector',
    },
    shareModal: {
      title: 'Share Exhibition',
      closeAria: 'Close share dialog',
      downloadQr: 'Download QR Code (PNG)',
      publicLinkLabel: 'Public Exhibition Link',
      copyLink: 'Copy Link',
      copied: 'Copied!',
      qrAlt: 'QR code for {title}',
      exploreShareText: 'Explore "{title}" on REDA Virtual Gallery',
      socialShareText: 'Explore "{title}" on REDA Virtual Gallery: ',
      nativeShare: 'Share via Device…',
      social: {
        facebook: 'Share on Facebook',
        x: 'Share on X',
        linkedin: 'Share on LinkedIn',
        whatsapp: 'Share on WhatsApp',
        telegram: 'Share on Telegram',
        email: 'Share via Email',
      },
    },
    drivePicker: {
      pickFromDrive: 'Pick from Google Drive',
      opening: 'Opening…',
      shareGrantFailed: 'File selected, but granting the Reda service account access failed. The media may not load until you share it with the service account manually.',
    },
    settingsSidebar: {
      ariaLabel: 'Studio Keybindings & Controls',
      title: 'Studio 3D Controls',
      closeAria: 'Close settings',
      keyboardNav: 'Keyboard Navigation',
      remapHint: 'Click any button to remap key',
      keys: {
        forward: 'Move Forward',
        backward: 'Move Backward',
        strafeLeft: 'Strafe Left',
        strafeRight: 'Strafe Right',
        elevateUp: 'Elevate Up',
        lowerDown: 'Lower Down',
        sprintBoost: 'Sprint Boost',
      },
      rebindTitle: 'Click and press a key to rebind',
      pressKey: 'Press key…',
      mouseBehavior: 'Mouse Behavior & Speeds',
      rightClickAction: 'Right-Click Drag Action',
      rightClickActionDesc: 'When an artwork is focused, directly drag to move it in the 3D scene.',
      moveFocusedArtwork: 'Move Focused Artwork',
      alwaysPanCamera: 'Always Pan Camera',
      cameraMoveSpeed: 'Camera Movement Speed',
      cameraMoveSpeedDesc: 'WASD navigation speed in authoring mode',
      cameraPanSpeed: 'Camera Panning Speed',
      cameraPanSpeedDesc: 'Smooth panning speed when right-click dragging',
      dragSensitivity: 'Direct Move Sensitivity',
      dragSensitivityDesc: 'Sensitivity when right-click dragging artworks',
      cheatSheetTitle: 'Controls Cheat Sheet',
      cheatSheet: {
        leftClickLabel: 'Left Click',
        leftClick: 'Select artwork / click background or Esc to unfocus',
        middleDragLabel: 'Middle Mouse Drag',
        middleDrag: 'Orbit 360° around view / mouse focus',
        rightDragLabel: 'Right Mouse Drag',
        rightDrag: 'Move focused artwork (or pan camera when unfocused)',
        wasdLabel: 'WASD / Arrows',
        wasd: 'Roam camera freely in gallery',
        shiftLabel: 'Shift',
        shift: 'Sprint navigation speed boost',
        escLabel: 'Esc',
        esc: 'Unfocus to free navigation mode',
      },
      resetDefaults: 'Reset Defaults',
      done: 'Done',
    },
    roomImporter: {
      header: 'Exhibition Gallery Architecture',
      selectRoomTab: 'Select Room ({count} available)',
      addCustomTab: 'Add Custom Room GLB',
      chooseSpace: 'Choose a gallery space…',
      customRoomSuffix: '(Custom Room)',
      builtInSuffix: '(Built-in Gallery)',
      noRoomsHintPrefix: 'No rooms loaded yet. Click',
      noRoomsHintSuffix: 'to add one.',
      step1Name: '1. Room Name',
      namePlaceholder: 'e.g. Modernist Pavillion Room',
      step2Source: '2. 3D Model Source (Google Drive Link or Direct GLB URL)',
      pickGlbButton: 'Pick GLB from Google Drive',
      sourcePlaceholder: 'https://drive.google.com/file/d/1A2B3C... or https://example.com/model.glb',
      driveHintLabel: 'For Google Drive:',
      driveHintText: 'Ensure the file is shared with the Reda Service Account and paste the link here.',
      urlHintLabel: 'For Direct URLs:',
      urlHintText: 'Must be a publicly accessible',
      urlHintSuffix: 'file ending in',
      step3Check: '3. (Optional) Check Local GLB Specs & Size',
      validating: 'validating…',
      checkingGlb: 'checking GLB format…',
      validGlb: 'Valid glTF 2.0 Binary',
      descriptionLabel: 'Description (Optional)',
      descriptionPlaceholder: 'Lighting details, architectural features, or room dimensions',
      saving: 'Saving Room…',
      saveAndSelect: 'Save & Select Room',
      errors: {
        missingSource: 'Please enter a Google Drive link, file ID, or 3D model URL.',
        glbValidationFailed: 'GLB file validation failed.',
        networkError: 'Network error while creating room.',
      },
      validation: {
        tooLarge: 'File is {sizeMb} MB — exceeds the 200 MB hard cap. Decimate geometry or reduce texture sizes and re-export.',
        notGlb: 'File does not appear to be a valid GLB. The first 4 bytes must be "glTF". Ensure you exported as glTF Binary (.glb), not .gltf.',
        sizeWarning: 'File is {sizeMb} MB — above the 100 MB soft budget. Reduce texture sizes or decimate geometry before uploading.',
      },
    },
    hotspotTransitionPreview: {
      liveTitle: 'Live preview of {transition} transition',
      liveSimulation: 'Live Simulation',
    },
    model3dHotspotEditor: {
      instructions: 'Click the model to drop a pin, click a gold pin to edit it, or click the surface again to move a selected pin.',
      loading: 'Loading 3D model…',
      loadFailed: 'Failed to load model.',
      loadFailedWithDetail: 'Failed to load model: {detail}',
    },
    setupSheet: {
      title: 'Exhibition Settings',
      publicLink: 'Public Link:',
      titleLabel: 'Exhibition Title *',
      curatorLabel: 'Curator Name',
      curatorPlaceholder: 'e.g. Elena Rostova',
      roomLabel: '3D Gallery Space / Room *',
      chooseRoom: '-- Choose 3D Gallery Space --',
      publicSuffix: '(Public)',
      customSuffix: '(Custom)',
      ambientAudioLabel: 'Room Ambient Audio (Optional)',
      driveOrIdPlaceholder: 'Paste Google Drive link or file ID',
      pickAudio: 'Pick Audio',
      ambientAudioHint: 'Plays on loop when a visitor enters the exhibition. Loops automatically.',
      spawnPointTitle: '3D Starting Vantage Point',
      customStart: 'Custom Start: {coords}',
      roomDefaultStart: 'Room Default: {coords}',
      defaultEntrance: 'Default room entrance position',
      resetSpawnTitle: 'Reset custom start point to room default',
      resetToDefault: 'Reset to Default',
      formatLabel: 'Exhibition Format',
      formatAriaLabel: 'Solo or Group Exhibition',
      formatSolo: 'Solo Artist',
      formatGroup: 'Group / Collective',
      artistsInExhibition: 'Artists in Exhibition',
      artistCount: '{count} artist profile(s) configured.',
      manageArtists: 'Manage Artists',
      descriptionLabel: 'Curatorial Statement / Description',
      descriptionPlaceholder: 'Enter curatorial wall text or retrospective overview…',
      introVideoLabel: 'Intro Video File ID or URL (Optional)',
      pasteDriveOrId: 'Paste Google Drive link or ID',
      pickVideo: 'Pick Video',
      introTransitionLabel: 'Intro Cinema Transition',
      saving: 'Saving…',
      saveDetails: 'Save Exhibition Details',
      toastSaved: 'Exhibition settings saved successfully.',
      errors: {
        missingTitle: 'Exhibition title cannot be empty.',
        saveFailed: 'Error saving settings: {detail}',
        networkError: 'Network error while saving exhibition settings.',
      },
      introPreview: {
        liveLabel: 'Live Transition Preview',
        replayTitle: 'Replay Transition Animation',
        replay: 'Replay',
        galleryWall: 'Gallery Wall',
        galleryRoom: '3D Gallery Room',
        curatorIntroVideo: 'Curator Intro Video',
        phaseVideo: '1. Video Playing',
        phaseTransitioning: '2. Transitioning (2.0s)…',
        phaseGallery: '3. Entered 3D Space',
      },
    },
    artistInspector: {
      newProfile: 'New Artist Profile',
      newProfileHint: 'Add artist bio and attribution details',
      portfolioBio: 'Artist Portfolio & Bio',
      portraitAlt: 'Portrait',
      pickPortrait: 'Pick portrait',
      nameLabel: 'Artist Name *',
      namePlaceholder: 'e.g. Trần Văn Cẩn',
      datesLabel: 'Life Dates / Active Years',
      datesPlaceholder: 'e.g. 1910–1994',
      quoteLabel: 'Artist Quote',
      quotePlaceholder: 'e.g. Art is the essence of life...',
      bioLabel: 'Biography',
      bioPlaceholder: 'Curatorial biography...',
      contactLabel: 'Contact / Web / Socials',
      contactPlaceholder: 'e.g. Hanoi | website.com | @handle',
      assignedWorks: 'Assigned Works ({count})',
      clickToEdit: 'Click to edit in Curate mode',
      noLinkedWorks: 'No artworks linked to this artist yet. Link artworks in Curate mode.',
      editInCurateMode: 'Edit "{title}" in Curate mode',
      inRoom: 'In Room',
      storage: 'Storage',
      saving: 'Saving…',
      addArtist: 'Add Artist',
      saveProfile: 'Save Profile',
      confirmDelete: 'Are you sure you want to delete profile for "{name}"?',
      toastCreated: 'Added artist profile "{name}".',
      toastUpdated: 'Saved changes to artist profile "{name}".',
      toastDeleted: 'Deleted artist profile "{name}".',
      errors: {
        missingName: 'Artist name cannot be empty.',
        createFailed: 'Error creating profile: {detail}',
        updateFailed: 'Error updating profile: {detail}',
        deleteFailed: 'Error deleting artist: {detail}',
        saveNetworkError: 'Network error while saving artist profile.',
        deleteNetworkError: 'Network error while deleting artist profile.',
      },
    },
    workbenchTopBar: {
      backToDashboard: 'Return to Exhibitions Dashboard',
      exhibitions: 'Exhibitions',
      viewModeAria: 'View mode',
      modeArtworks: 'Artworks',
      modeArtworksTitle: 'Position, rotate, and scale artworks on walls',
      modeWaypoints: 'Waypoints',
      modeWaypointsTitle: 'Place and fine-tune start point and tour waypoints',
      saved: 'Saved',
      preview: 'Preview',
      share: 'Share',
      unpublish: 'Unpublish',
      publishing: 'Publishing…',
      publish: 'Publish',
      dashboard: 'Dashboard',
    },
    workbench: {
      loading: 'Loading workbench…',
      toastPublished: 'The exhibition has been published publicly.',
      toastPublishedTitle: 'Published successfully',
      toastPublishedAction: 'Share / View',
      toastUnpublished: 'The exhibition has been moved back to draft (Unpublished).',
      toastRoomChanged: 'Switched space to "{name}".',
      publicRoom: 'Public Room',
      customSpace: 'Custom Space',
      badgeWaypoints: 'Waypoints mode · Visitor Path & Start',
      badgeArtworks: 'Artworks mode · Placement',
      autoSaved: 'Auto-saved',
      errors: {
        publishFailed: 'Could not update publish status.',
        publishNetworkError: 'Network error while updating publish status.',
        roomChangeFailed: 'Could not switch gallery room.',
        roomChangeNetworkError: 'Network error while switching gallery room.',
      },
    },
    artworkForm: {
      roomPlacement: 'Room Placement',
      placedInRoom: 'Placed in 3D Room',
      inStorageUnplaced: 'In Storage (Unplaced)',
      moveToStorageTitle: 'Move this artwork to storage (removes from 3D room)',
      placeInRoomTitle: 'Place this artwork into the 3D gallery room',
      moveToStorage: 'Move to Storage',
      placeInRoom: 'Place in Room',
      liveFramePreview: 'Live Frame Preview',
      framedPreviewAlt: 'Framed Preview',
      interactiveHotspots: 'Interactive Hotspots',
      hotspotsPinnedOnModel: '{count} details pinned on the model',
      hotspotsPinnedOnCanvas: '{count} details pinned on canvas',
      editHotspots: 'Edit Hotspots',
      mediumTypeLabel: 'Artwork medium type',
      mediumTypeAria: 'Artwork Medium Type',
      type2d: '2D Painting / Image',
      typeVideo: 'Video',
      type3d: '3D Model',
      imageLinkLabel: 'Google Drive Image Link or File ID',
      pickFromDrive: 'Pick from Drive',
      driveOrIdPlaceholder: 'https://drive.google.com/file/d/... or bare file ID',
      serviceAccountHint: 'Ensure the file is shared with the Reda Service Account in Google Drive.',
      youtubeLinkLabel: 'YouTube Video Link or Video ID',
      youtubePreviewAlt: 'YouTube Video Preview',
      modelLinkLabel: '3D Model (.glb) Google Drive Link or File ID',
      audioGuideLabel: 'Audio Guide Narration (Optional Google Drive File ID)',
      audioGuidePlaceholder: 'Google Drive link or ID for optional voiceover / audio guide',
      frameSettingsTitle: 'Frame & Placard Settings',
      frameMaterial: 'Frame Material',
      frameWood: 'Natural Wood',
      frameGold: 'Ornate Gold',
      frameMetalBlack: 'Matte Black',
      frameFloatWhite: 'Minimal White',
      frameCanvasWrap: 'Canvas Wrap',
      frameNone: 'Frameless',
      frameWidth: 'Frame Width (m)',
      showPlacard: 'Display Wall Placard under artwork',
      allowTilt: 'Enable 3D Perspective Tilt in Inspect Mode',
      artworkInfoTitle: 'Artwork Info',
      titleLabel: 'Title *',
      titlePlaceholder: 'Artwork title',
      linkedArtistLabel: 'Link to Exhibition Artist Profile',
      noLinkedArtist: '-- No linked artist profile --',
      mediumLabel: 'Medium',
      mediumPlaceholder: 'e.g. Oil on linen, Digital canvas',
      yearLabel: 'Year',
      yearPlaceholder: 'e.g. 2024',
      dimensionsLabel: 'Physical Dimensions',
      dimensionsPlaceholder: 'e.g. 120 × 90 cm',
      descriptionLabel: 'Description / Curator Statement',
      descriptionPlaceholder: 'Background, historical notes, or thematic context displayed in focus panel',
      deleteTitle: 'Permanently remove artwork from this exhibition',
      saving: 'Saving…',
      saveChanges: 'Save Changes',
      addToExhibition: 'Add to Exhibition',
      untitled: 'Untitled',
      thisArtwork: 'this artwork',
      confirmDelete: 'Are you sure you want to permanently delete "{title}"?',
      toastRemoved: 'Removed artwork "{title}" from the exhibition.',
      toastSaved: 'Saved changes to artwork.',
      toastAdded: 'Added artwork to exhibition.',
      errors: {
        invalidImage: 'Please provide a valid image link or Google Drive ID.',
        invalidYoutube: 'Please provide a valid YouTube link or an 11-character video ID.',
        missingModel: 'Please select a 3D model file (.glb) from Google Drive.',
        proxyStillGenerating: 'Generating the 3D proxy, please wait before saving.',
        deleteFailed: 'Could not delete artwork.',
        deleteNetworkError: 'Network error while deleting artwork.',
        saveFailed: 'Error saving artwork: {detail}',
        saveNetworkError: 'Network error while saving artwork.',
      },
      model3d: {
        generatingProxy: 'Generating a lightweight proxy for the 3D model…',
        processingTitle: 'Processing 3D Model',
        proxyReady: '3D model is ready — the lightweight proxy has been generated and uploaded.',
        uploadSuccessTitle: 'Model uploaded successfully',
        proxyGenerationFailed: 'Could not generate the lightweight proxy.',
        errors: {
          glbCompressed: 'Could not read this .glb. If it uses Draco or meshopt compression, please export an uncompressed .glb — compressed sources are not supported yet.',
          driveAuthExpired: 'Drive authorization expired — re-open the picker and try again.',
        },
        processingErrorTitle: '3D Model Processing Error',
        generatingProxyShort: 'Generating low-poly proxy…',
        proxyReadyShort: 'Proxy ready ({id}).',
        proxyGenerationFailedShort: 'Failed to generate low-poly proxy.',
      },
    },
    hotspotEditor: {
      title: 'Interactive Hotspot Editor',
      artworkLabel: 'Artwork: {title}',
      transitionSaved: 'Transition saved',
      transitionAnimationLabel: 'Transition Animation:',
      saving: 'Saving…',
      existingHotspots: 'Existing Hotspots ({count})',
      untitledHotspot: 'Untitled hotspot',
      canvasInstruction: 'Click anywhere on the artwork image to drop a new interpretive hotspot pin.',
      zoomGroup: 'Image zoom',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      zoomFit: 'Fit',
      dragToReposition: 'Drag to reposition',
      noImage: 'No image file associated with this artwork.',
      doneEditing: 'Done Editing Hotspots',
      newPinTitle: 'New Hotspot Pin',
      locationModelSurface: 'Location: dropped on 3D model surface',
      locationXY: 'Location: X: {x}%, Y: {y}%',
      hotspotTitleLabel: 'Hotspot Title',
      titlePlaceholder: 'e.g. Signature, Craquelure, Symbolism',
      interpretiveTextLabel: 'Interpretive Text',
      descriptionPlaceholder: 'Description displayed when visitor clicks this pin',
      audioSegmentLabel: 'Option A: Audio Guide Segment (Seconds)',
      startPlaceholder: 'Start (e.g. 42.5)',
      stopPlaceholder: 'Stop (optional)',
      to: 'to',
      audioSegmentHint: 'Jump to a point in the main audio guide. Leave Stop empty to play to the end.',
      dedicatedAudioLinkLabel: 'Option B: Dedicated Audio File Link (Google Drive / URL)',
      pickAudioFromDrive: 'Pick Audio from Google Drive',
      audioUrlPlaceholder: 'https://drive.google.com/file/d/... or direct audio URL',
      dedicatedAudioHint: 'You can provide a dedicated audio clip narration specific to this hotspot.',
      addPin: 'Add Hotspot Pin',
      editTitle: 'Edit Hotspot',
      pinAnchoredModel: 'Pin anchored to 3D model surface',
      pinAtXY: 'Pin at: X: {x}%, Y: {y}%',
      repositionHintModel: 'Click a new spot on the 3D model to move this pin, then Save Changes.',
      repositionHintImage: 'Drag the highlighted pin on the image to reposition it, then Save Changes.',
      dedicatedAudioFileLabel: 'Option B: Dedicated Audio File',
      audioDirectUrlPlaceholder: 'Google Drive link or direct URL',
      confirmDeleteQuestion: 'Confirm Delete?',
      saveChanges: 'Save Changes',
      emptyStateModel: 'Click on the 3D model to place a new pin, or select an existing pin to edit or delete it.',
      emptyStateImage: 'Click on the image to place a new pin, or click an existing pin to edit or delete it.',
      totalHotspots: 'Total hotspots on this artwork: {count}',
      deletedMsg: 'Deleted "{title}"',
      hotspotFallback: 'hotspot',
      undo: 'Undo',
      defaultTitle: 'Detail Hotspot',
      detailFallback: 'Detail',
      errors: {
        updateFailed: 'Error updating hotspot: {detail}',
        updateNetworkError: 'Network error while updating hotspot.',
        createFailed: 'Error creating hotspot: {detail}',
        createNetworkError: 'Network error while creating hotspot.',
        deleteFailed: 'Could not delete hotspot.',
        deleteNetworkError: 'Network error while deleting hotspot.',
        restoreFailed: 'Could not restore hotspot.',
        restoreNetworkError: 'Network error while restoring hotspot.',
      },
      toastUpdated: 'Saved changes to hotspot "{title}".',
      toastCreated: 'Added hotspot "{title}".',
      toastRestored: 'Restored hotspot "{title}".',
    },
    gizmoPlacement: {
      backToArtworks: 'Back to Artworks',
      roomBadge: 'Room: {name}',
      moveStartPositionTitle: 'Move Start Position',
      movePosition: 'Move Position',
      rotateStartFacingTitle: 'Rotate Start Facing Direction',
      rotateFacing: 'Rotate Facing',
      setAtCameraTitle: 'Drop start vantage point directly at current camera position',
      setAtCamera: 'Set at Camera',
      positionGizmoTitle: 'Position Translation Gizmo',
      move: 'Move',
      rotationGizmoTitle: 'Rotation Gizmo',
      rotate: 'Rotate',
      scaleGizmoTitle: 'Scale Gizmo',
      scale: 'Scale',
      lockedTitle: 'Locked: Aspect ratio is preserved proportionally',
      unlockedTitle: 'Unlocked: Free independent axis scaling',
      lockRatio: 'Lock Ratio',
      freeScale: 'Free Scale',
      unfocusTitle: 'Unfocus / Deselect (Esc)',
      unfocus: 'Unfocus',
      artworksModeIndicator: 'Artworks Mode · Click any artwork on the wall to position, scale, or rotate',
      configureKeybindingsTitle: 'Configure Keybindings & Controls',
      controlsAndKeys: 'Controls & Keys',
      hudTitleWaypoint: 'Gallery Start Point & Waypoint',
      hudTitleSelected: 'Selected: {title}',
      hudTitlePlacement: 'Artworks Placement Mode',
      hudStartPos: 'Start Pos: [{x}, {y}, {z}]',
      hudFacing: 'Facing: [{x}, {y}, {z}]',
      hudPos: 'Pos: [{x}, {y}, {z}]',
      hudRot: 'Rot: [{x}, {y}, {z}]',
      hudScale: 'Scale: [{x}, {y}, {z}]',
      hudUnfocusedPrefix: 'Click any artwork on the wall to position, scale, or rotate it. Switch to',
      hudUnfocusedSuffix: 'above to adjust visitor start point.',
      hint: {
        middleDrag: 'Middle-drag',
        orbit: 'Orbit',
        rightDrag: 'Right-drag',
        moveObject: 'Move object (or pan when unfocused)',
        leftClick: 'Left-click',
        select: 'Select',
        wasd: 'WASD',
        roam: 'Roam',
        esc: 'Esc',
        unfocus: 'Unfocus',
      },
      toastSpawnSaved: 'Saved start point: ({x}, {y}, {z})',
      errors: {
        spawnSaveFailed: 'Could not save start point.',
        spawnSaveNetworkError: 'Network error while saving start point.',
      },
    },
    artistViewerPreview: {
      newArtist: 'New Artist',
      artistNameFallback: 'Artist Name',
      curateNewDossier: 'Curate · New Artist Dossier',
      visitorPreviewNamed: 'Visitor Preview · {name}',
      visitorPreviewDossier: 'Visitor Preview · Artist Dossier',
      deviceViewportAria: 'Preview device viewport',
      previewPcTitle: 'Preview Desktop / PC Visitor View',
      previewMobileTitle: 'Preview Mobile Landscape Visitor View',
      pcView: 'PC View',
      mobileLandscape: 'Mobile Landscape',
      archiveDossier: 'Archive · Artist Dossier',
      noArtistSelected: 'No Artist Selected',
      noArtistHint: 'Select an artist from the left archive or create a new profile to preview how their biography, portrait, and quotes appear to visitors in the 3D gallery.',
      closeArtistProfile: 'Close artist profile',
      bioUnavailable: 'Biography not available for this artist.',
      worksInExhibition: 'Works in Exhibition ({count})',
    },
  },
  viewer: {
    untitledArtist: 'Untitled Artist',
    hud: {
      openingExhibitionAria: 'Opening exhibition…',
      galleryCanvasAria: '3D gallery',
      movementInstructionsAria: 'Movement instructions',
      toggleCameraModeTitle: 'Toggle Camera Mode (Shortcut: C)',
      fpsMode: 'FPS Mode',
      galleryMode: 'Gallery Mode',
      walk: 'walk',
      mouseLooks: 'Mouse looks',
      clickArtToFocus: 'Click art to focus',
      unlockCursor: 'unlock cursor',
      drag: 'Drag',
      look: 'look',
      clickArtBold: 'Click art',
      toFocusAngle: 'to focus (90°)',
      clickFloorBold: 'Click floor',
      toTeleport: 'to teleport',
      gallerySettings: 'Gallery Settings',
      catalog2d: '2D Catalog',
      exhibitionAlt: '3D exhibition: {title}',
    },
    focus: {
      controlsAria: 'Focus mode controls',
      exitDetailView: 'Exit detail view',
      exitFocusViewTitle: 'Exit focus view',
      showArtworkInfoAria: 'Show artwork information',
      closeArtworkInfoAria: 'Close artwork information',
      artworkDetailsTitle: 'Artwork details',
      pauseNarrationTitle: 'Pause narration',
      playNarrationTitle: 'Play narration',
      pauseAudioNarrationAria: 'Pause audio narration',
      playAudioNarrationAria: 'Play audio narration',
      artworkInfoAria: 'Artwork information: {title}',
      closeInfoCard: 'Close information card',
      mediumLabel: 'Medium:',
      dimensionsLabel: 'Dimensions:',
      readArtistBioTitle: 'Read biography of {name}',
      readArtistBio: 'Read Artist Bio',
      openCinemaMode: 'Open Cinema Mode',
      inspectIn360: 'Inspect in 360',
      inspectFullResolution: 'Inspect Full Resolution',
      navigationAria: 'Artwork navigation',
      previousArtwork: 'Previous artwork',
      nextArtwork: 'Next artwork',
    },
    inspect: {
      dialogAria: 'Inspect: {title}',
      detailCounter: 'Detail {current} of {total}',
      inspectModeEyebrow: 'Inspect Mode',
      aboutArtist: 'About {name}',
      readAboutArtistTitle: 'Read about {name}',
      toggleHotspotsDirectory: 'Toggle Hotspots Directory',
      hotspotsListCount: 'Hotspots List ({count})',
      closeInspectAria: 'Close inspect',
      exitInspectModeTitle: 'Exit Inspect Mode',
      hotspotsDirectoryAria: 'Hotspots Directory',
      hotspotsDirectoryTitle: 'Hotspots Directory',
      closeDirectoryAria: 'Close directory',
      clickDetailToZoom: 'Click any detail point to zoom and inspect.',
      audioAttached: 'Audio Attached',
      resetViewTitle: 'Reset zoom, tilt, and framing',
      resetView: 'Reset View',
      tiltActiveTitle: 'Tilt active: drag to angle in 3D. Click to switch to pan.',
      toggle3dTiltTitle: 'Toggle 3D perspective tilt',
      tiltActive: 'Tilt Active',
      tilt3d: '3D Tilt',
      previousDetailTitle: 'Previous Detail',
      prev: 'Prev',
      openHotspotsDirectoryTitle: 'Click to open hotspots directory',
      detailsCount: 'Details ({count})',
      overview: 'Overview',
      detailListLabel: '{index}. {title}',
      nextDetailTitle: 'Next Detail',
      next: 'Next',
      pauseDetailAudioTitle: 'Pause Detail Audio',
      listenDetailAudioTitle: 'Listen to Detail Audio',
      pauseClip: 'Pause Clip',
      listen: 'Listen',
      jumpToMainGuideTitle: 'Jump to {sec}s in Main Audio Guide',
      guideSeconds: 'Guide ({sec}s)',
      seeFullDescriptionTitle: 'See full description',
      seeMore: '... See more',
      seeLess: 'See less',
      hintCinema: 'Cinema Mode · Press Esc or click to return to gallery',
      hintMobileTiltActive: 'Drag to Tilt in 3D · Pinch to Zoom · Tap "Tilt Active" to return to Pan',
      hintMobileTiltInactive: 'Drag to Pan · Pinch to Zoom · Tap "3D Tilt" to angle',
      hintMobilePanOnly: 'Drag to Pan · Pinch to Zoom',
      hintDesktopTilt: 'Left-drag to Pan · Right-drag to Tilt · Scroll to Zoom',
      hintDesktopPanOnly: 'Left-drag to Pan · Scroll to Zoom',
      sidebar: {
        detailsAria: 'Hotspot Details',
        detailCounter: 'Detail {current} of {total}',
        dragToMove: '(Drag to move)',
        expandFullPanelTitle: 'Expand full panel',
        minimizeToCompactTitle: 'Minimize to compact card',
        expand: 'Expand',
        minimize: 'Minimize',
        closePanelTitle: 'Close panel',
        dedicatedAudioLabel: 'Dedicated Hotspot Audio',
        jumpToMainGuide: 'Jump to {sec}s in Main Audio Guide',
        prev: 'Prev',
        next: 'Next',
      },
    },
    model360: {
      dialogAria: '360° inspect: {title}',
      eyebrow: '360° Inspect',
      toggleHotspotsDirectory: 'Toggle Hotspots Directory',
      hotspotsListCount: 'Hotspots List ({count})',
      closeAria: 'Close 360 inspect',
      exitTitle: 'Exit 360 Inspect',
      hotspotsDirectoryAria: 'Hotspots Directory',
      hotspotsDirectoryTitle: 'Hotspots Directory',
      closeDirectoryAria: 'Close directory',
      clickDetailToOrbit: 'Click any detail point to orbit and inspect.',
      audioAttached: 'Audio Attached',
      loadingModel: 'Loading 3D model…',
      noModelFile: 'No model file available.',
      failedToLoadWithMsg: 'Failed to load 3D model: {error}',
      failedToLoadGeneric: 'Failed to load 3D model.',
      hint: 'Left-drag to orbit · Right-drag to pan · Scroll to zoom',
      hotspotLabel: 'Hotspot: {title}',
    },
    catalog: {
      exhibitionCatalogAria: 'Exhibition catalog',
      curatedBy: 'Curated by {name}',
      showing2dAccessible: 'Showing 2D accessible catalog.',
      webglRequiredNotice: '3D view requires WebGL2. Showing the 2D catalog.',
      enter3dGallery: 'Enter 3D Gallery',
      audioGuideAria: 'Audio guide: {title}',
    },
    audio: {
      ariaLabel: 'Audio guide: {title}',
      kicker: 'Audio Guide',
      pauseNarrationAria: 'Pause narration',
      playNarrationAria: 'Play narration',
      pauseNarrationTitle: 'Pause narration',
      playNarrationTitle: 'Play audio narration',
      progressSliderAria: 'Audio progress slider',
      muteToggle: 'Mute',
      unmuteToggle: 'Unmute',
      defaultTitle: 'Audio Narration',
    },
    artist: {
      closeProfileAria: 'Close artist profile',
      bioUnavailable: 'Biography not available for this artist.',
    },
    intro: {
      dialogAria: 'Exhibition Intro',
      loadingSpace: 'Loading 3D Exhibition Space…',
      defaultTitle: 'Virtual Exhibition',
      curatedBy: 'Curated by {name}',
      enterExhibitionAria: 'Enter Exhibition',
      enterExhibition: 'Enter Exhibition',
      preparingGallerySpace: 'Preparing 3D gallery space…',
      finalizingTextures: 'Finalizing 3D artwork textures…',
    },
    loadingCurtain: {
      dialogAria: 'Exhibition Loading',
      emblemAlt: 'Reda Gallery',
      defaultTitle: 'Virtual Exhibition',
      curatedBy: 'Curated by {name}',
      openingArchive: 'Opening Curatorial Archive…',
      streamingGeometry: 'Streaming 3D Spatial Geometry…',
      illuminating: 'Illuminating Gallery Spotlights & Frames…',
      galleryPrepared: 'Gallery Room Prepared',
    },
    error: {
      dialogAria: 'Exhibition Error',
      notFoundTitle: 'Exhibition Not Found',
      notFoundMessage: 'This exhibition link does not exist or has moved to another gallery room.',
      privateTitle: 'Private Exhibition',
      privateMessage: 'This exhibition is currently in preparation or open only to authorized visitors.',
      networkTitle: 'Connection Error',
      networkMessage: 'Could not load the exhibition space data. Please check your connection and try again.',
      returnHome: 'Return to Home',
      studioLogin: 'Studio Login',
      retryConnection: 'Retry connection',
    },
    joystick: {
      ariaLabel: 'Virtual navigation joystick',
      move: 'Move',
    },
    settings: {
      dialogAria: 'Viewer Settings',
      header: 'Gallery & Control Settings',
      closeAria: 'Close settings',
      cameraDisplay: 'Camera & Display',
      fov: 'Field of View (FOV)',
      fovAria: 'Field of View (FOV)',
      narrow: 'Narrow (50°)',
      defaultLabel: 'Default (65°)',
      wide: 'Wide (90°)',
      movementSpeed: 'Movement Speed (WASD / Arrows)',
      walkingSpeed: 'Walking Speed',
      walkingSpeedAria: 'Walking Speed',
      slow: 'Slow',
      normal: 'Normal',
      fast: 'Fast',
      sprintSpeed: 'Sprint Speed (Holding Shift)',
      sprintSpeedAria: 'Sprint Speed',
      desktopControls: 'Desktop Camera & Mouse Look',
      cameraControlMode: 'Camera Control Mode',
      fpsPointerLock: 'FPS (Pointer Lock)',
      galleryDragLook: 'Gallery (Drag Look)',
      galleryModeBtn: 'Gallery Mode',
      fpsModeBtn: 'FPS Mode (Pointer Lock)',
      mouseSensitivity: 'Mouse Sensitivity',
      mouseSensitivityAria: 'Mouse Sensitivity',
      invertHorizontal: 'Invert Horizontal (X)',
      invertVertical: 'Invert Vertical (Y)',
      mobileTouch: 'Mobile Touch Drag Look',
      resetDefaults: 'Reset to Defaults',
      done: 'Done',
    },
    hotspot: {
      closeAria: 'Close hotspot',
      interpretivePoint: 'Interpretive Point',
      jumpToAudioGuide: 'Jump to {sec}s in audio guide',
      label: 'Hotspot: {title}',
    },
  },
};
